import React from "react";

/** @jsx jsx */
import { jsx, css, keyframes } from '@emotion/react';
import { Colors } from "../palettes/colorPalette";
import { UIConstants } from "../../../shared/constants";

const shakeAnimation = keyframes`
  0%, 100% {
    transform: translateX(0);
  }
  10%, 30%, 50%, 70%, 90% {
    transform: translateX(-3px);
  }
  20%, 40%, 60%, 80% {
    transform: translateX(3px);
  }
`;

const circleStyle = css`
  width: 27px;
  filter: drop-shadow(2px 4px 2px ${Colors.UICarbShadow})
`;

const keys = keyframes`
	0% {
		transform: translateY(0);
	}

	100% {
		transform: translateY(-8px);
	}
`;

const bounce = css`
	animation: ${keys} 0.5s infinite alternate ease-in-out;
`;


type GoldCounterProps = {
  count: number,
  hovered: number,
  max: number,
  justGained: number,
  shake: boolean,
  setShake: (shake: boolean) => void,
}

export const GoldCounter = (props: GoldCounterProps) => {
  const spentMoney = props.justGained < 0;
  const loss = spentMoney ? -props.justGained : 0;
  const gain = spentMoney ? 0 : props.justGained;
  const changeInMoney = Math.abs(props.justGained);

  const highlighted = Math.max(props.hovered, gain);
  const activeCount = Math.max(0, Math.min(props.count, props.count - highlighted + loss));
  const changeCount = Math.min(changeInMoney, props.count + loss);
  const hoveredCount = Math.min(props.count, Math.max(0, props.hovered - changeCount));
  const inactiveCount = props.max - (activeCount + hoveredCount + changeCount);

  // console.log("activeCount: " + activeCount + " changeCount: " + changeCount + " hoveredCount: " + hoveredCount + " inactiveCount: " + inactiveCount + " highlighted: " + highlighted
  //  + " props.count: " + props.count + " props.max: " + props.max + " props.hovered: " + props.hovered + " changeInMoney: " + changeInMoney + " spentMoney: " + spentMoney);

  React.useEffect(() => {
    const goldCounter = document.getElementById("gold-counter");
    goldCounter?.addEventListener('animationend', () => {
      props.setShake(false);
    });
  }, []);

  const containerStyle = css`
      width: fit-content;
      display: flex;
      flex-direction: row;
      gap: .25vw;
      ${props.shake ? css`
        animation: ${shakeAnimation} .5s;
      ` : ""}
  `;

  return (
    <div id={'gold-counter'} css={containerStyle}>
      {
        Array.from({ length: activeCount }, (_, index) =>
          <svg key={`active_${index}`} css={circleStyle} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <circle cx="50" cy="50" r="50" fill={Colors.UICarb} />
            <circle cx="50" cy="50" r="30" fill={Colors.UICarbInner} />
          </svg>)
      }
      {props.hovered > 0 &&
        Array.from({ length: hoveredCount }, (_, index) =>
          <svg key={`hovered_${index}`} css={[circleStyle, bounce]} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <circle cx="50" cy="50" r="50" fill={Colors.UICarbSpending} />
          </svg>)
      }
      {changeInMoney > 0 &&
        Array.from({ length: changeCount }, (_, index) =>
          <svg key={`gained_${index}`} css={circleStyle} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <circle cx="50" cy="50" r="50" fill={Colors.UICarb}>
              <animate
                attributeName="fill"
                values={spentMoney ? `${Colors.UICarb};${Colors.UICarbSpent}` : `${Colors.UICarbSpent};${Colors.UICarb}`}
                dur={UIConstants.GainMoneyAnimationSeconds}
                repeatCount="0" />
            </circle>
          </svg>)
      }
      {props.max > props.count &&
        Array.from({ length: inactiveCount }, (_, index) =>
          <svg key={`inactive_${index}`} css={circleStyle} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <circle cx="50" cy="50" r="50" fill={Colors.UICarbSpent} />
          </svg>)
      }
    </div>)
};

